import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ApiError } from '@vmgate/common';

const schema = z.object({
  email: z.string().email('Valid email required'),
  password: z.string().min(1, 'Password required')
});

type LoginValues = z.infer<typeof schema>;

export function LoginPage() {
  const { login, account } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: ''
    }
  });

  useEffect(() => {
    const flash = sessionStorage.getItem('vmgate:customerAuthMessage');
    if (flash) {
      setInfo(flash);
      sessionStorage.removeItem('vmgate:customerAuthMessage');
    }
  }, []);

  useEffect(() => {
    if (account) {
      navigate('/', { replace: true });
    }
  }, [account, navigate]);

  const onSubmit = async (values: LoginValues) => {
    setError(null);
    try {
      await login(values);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Invalid email or password.');
      } else {
        setError('Unable to sign in right now. Please try again.');
      }
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #0ea5e9 100%)',
        padding: '2rem'
      }}
    >
      <div className="surface" style={{ width: '100%', maxWidth: '400px', borderRadius: '18px' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div
            style={{
              width: '54px',
              height: '54px',
              margin: '0 auto 1rem',
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(14, 165, 233, 0.16)',
              color: '#0ea5e9',
              fontWeight: 600
            }}
          >
            VM
          </div>
          <h1 style={{ margin: 0 }}>Customer Portal</h1>
          <p className="text-muted" style={{ marginTop: '0.5rem' }}>
            Sign in to review your licenses and devices.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'grid', gap: '1rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <span style={{ fontWeight: 600 }}>Email</span>
            <input
              type="email"
              {...register('email')}
              autoComplete="email"
              placeholder="you@company.com"
              style={inputStyle}
            />
            {errors.email && <span style={errorStyle}>{errors.email.message}</span>}
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <span style={{ fontWeight: 600 }}>Password</span>
            <input
              type="password"
              {...register('password')}
              autoComplete="current-password"
              placeholder="••••••••"
              style={inputStyle}
            />
            {errors.password && <span style={errorStyle}>{errors.password.message}</span>}
          </label>

          {info && (
            <div style={{ color: '#047857', background: 'rgba(5, 150, 105, 0.12)', padding: '0.6rem', borderRadius: '10px' }}>{info}</div>
          )}

          {error && <div style={{ ...errorStyle, background: 'rgba(220, 38, 38, 0.12)', padding: '0.6rem', borderRadius: '10px' }}>{error}</div>}

          <button
            type="submit"
            style={{
              border: 'none',
              borderRadius: '10px',
              background: 'var(--color-primary)',
              color: '#fff',
              padding: '0.75rem',
              fontSize: '1rem',
              cursor: 'pointer'
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: '10px',
  padding: '0.65rem 0.85rem',
  fontSize: '1rem',
  background: '#fff'
};

const errorStyle: React.CSSProperties = {
  color: '#b91c1c',
  fontSize: '0.85rem'
};

